import React, { useContext, useRef, useEffect } from 'react';
import { InlineEditContext } from './InlineEditContext';

type AllowedTag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'a' | 'div';

interface Props {
  /** The control key in section.controls that this text maps to */
  controlKey: string;
  /** HTML tag to render as */
  tag?: AllowedTag;
  style?: React.CSSProperties;
  className?: string;
  children: React.ReactNode;
}

/**
 * InlineEditText — wraps any text element.
 * When the parent section is selected (isEditMode = true), the element becomes
 * contentEditable. On blur, it calls onControlChange with the updated text.
 */
const InlineEditText: React.FC<Props> = ({
  controlKey,
  tag: Tag = 'span',
  style,
  className,
  children,
}) => {
  const ctx = useContext(InlineEditContext);
  const ref = useRef<HTMLElement>(null);
  const isEditable = ctx?.isEditMode ?? false;

  // Sync children → DOM when not focused (controls update from Properties panel)
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    const text = typeof children === 'string' ? children : String(children ?? '');
    if (el.innerText !== text) {
      el.innerText = text;
    }
  }, [children]);

  const handleBlur = () => {
    if (!ctx || !ref.current) return;
    const newValue = ref.current.innerText;
    ctx.onControlChange(ctx.sectionId, controlKey, newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Allow Enter to commit (for single-line elements like h1)
    if (e.key === 'Enter' && (Tag === 'h1' || Tag === 'h2' || Tag === 'h3' || Tag === 'h4')) {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
    // Escape — revert and blur
    if (e.key === 'Escape') {
      if (ref.current) {
        ref.current.innerText = typeof children === 'string' ? children : String(children ?? '');
      }
      (e.target as HTMLElement).blur();
    }
    // Stop propagation so canvas click-outside doesn't deselect section while typing
    e.stopPropagation();
  };

  const editableStyle: React.CSSProperties = isEditable
    ? {
        outline: 'none',
        cursor: 'text',
        borderRadius: 4,
        transition: 'box-shadow 0.15s',
      }
    : {};

  const focusStyle: React.CSSProperties = isEditable
    ? { '--inline-edit-focus-shadow': '0 0 0 2px rgba(37,99,235,0.5)' } as React.CSSProperties
    : {};

  return React.createElement(Tag, {
    ref,
    contentEditable: isEditable,
    suppressContentEditableWarning: true,
    onBlur: isEditable ? handleBlur : undefined,
    onKeyDown: isEditable ? handleKeyDown : undefined,
    onClick: isEditable ? (e: React.MouseEvent) => e.stopPropagation() : undefined,
    className: [
      className,
      isEditable ? 'inline-edit-active' : '',
    ].filter(Boolean).join(' '),
    style: {
      ...style,
      ...editableStyle,
      ...focusStyle,
    },
    'data-control-key': controlKey,
  }, children);
};

export default InlineEditText;
